import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { format } from 'date-fns';
import { Edit2, Plus, Eye, ChevronRight, ChevronLeft } from 'lucide-react';

function CreateContacts() {
    const [csvData, setCsvData] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [campaignNames, setCampaignNames] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [activeHeader, setActiveHeader] = useState(null);
    const [mailTags, setMailTags] = useState([]);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [selectedMailTag, setSelectedMailTag] = useState(null);
    const [headerNames, setHeaderNames] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newCampaign, setNewCampaign] = useState('');
    const dropdownRef = useRef(null);
    const [tempModalopen, setempModalopen] = useState(false);
    const [tableModalopen, setTableModalopen] = useState(false);
    const [campaignName, setCampaignName] = useState('');
    const [headerEnabled, setHeaderEnabled] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
    const [previewTemplate, setPreviewTemplate] = useState('');

    const apiUrl = process.env.REACT_APP_API_URL;
    const BUId = sessionStorage.getItem('BUId');

    const getEmailFromRecord = (record) => {
        const emailHeader = Object.keys(headerNames).find(header =>
            headerNames[header] === 'EmailAddress'
        );

        if (emailHeader && record[emailHeader]) {
            return record[emailHeader];
        }

        const possibleEmailFields = ['email', 'EmailAddress', 'Email', 'email_address'];
        for (const field of possibleEmailFields) {
            if (record[field]) {
                return record[field];
            }
        }

        return 'N/A';
    };

    const replaceMailTagsWithData = (templateContent, record) => {
        let previewContent = templateContent;

        // Create a mapping of all possible data sources
        const dataMapping = {};

        // 1. First, map data from explicitly mapped headers
        Object.keys(headerNames).forEach(csvHeader => {
            if (headerEnabled[csvHeader]) {
                const mailTag = headerNames[csvHeader];
                if (mailTag && record[csvHeader]) {
                    dataMapping[mailTag] = record[csvHeader];
                }
            }
        });

        Object.keys(record).forEach(header => {
            if (headerEnabled[header]) {
                if (mailTags.includes(header)) {
                    dataMapping[header] = record[header];
                }

                const normalizedHeader = header.toLowerCase().replace(/\s+/g, '');
                mailTags.forEach(tag => {
                    const normalizedTag = tag.toLowerCase().replace(/\s+/g, '');
                    if (normalizedHeader === normalizedTag) {
                        dataMapping[tag] = record[header];
                    }
                });
            }
        });

        // Replace all mail tags with their corresponding values
        Object.keys(dataMapping).forEach(tag => {
            // Handle different tag formats
            const patterns = [
                `[${tag}]`,           // [tag]
                `{${tag}}`,           // {tag}
                `{{${tag}}}`,         // {{tag}}
                `[${tag.toUpperCase()}]`,  // [TAG]
                `{${tag.toUpperCase()}}`,  // {TAG}
                `{{${tag.toUpperCase()}}}` // {{TAG}}
            ];

            patterns.forEach(pattern => {
                const regex = new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                previewContent = previewContent.replace(regex, dataMapping[tag]);
            });
        });


        mailTags.forEach(tag => {
            if (!dataMapping[tag]) {
                const patterns = [
                    `\\[${tag}\\]`,
                    `\\{${tag}\\}`,
                    `\\{\\{${tag}\\}\\}`,
                    `\\[${tag.toUpperCase()}\\]`,
                    `\\{${tag.toUpperCase()}\\}`,
                    `\\{\\{${tag.toUpperCase()}\\}\\}`
                ];

                patterns.forEach(pattern => {
                    const regex = new RegExp(pattern, 'g');
                    previewContent = previewContent.replace(regex, `[${tag}]`);
                });
            }
        });

        return previewContent;
    };
    const totalPages = Math.ceil(csvData.length / itemsPerPage);

    // Get current page data
    const paginatedData = csvData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Function to get page numbers for pagination display
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total pages are less than max visible
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Show pages around current page
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = startPage + maxVisiblePages - 1;

            if (endPage > totalPages) {
                endPage = totalPages;
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers;
    };
    const openPreviewModal = async () => {
        if (!selectedTemplate || csvData.length === 0) {
            displayError('Please select a template and upload data first.');
            return;
        }

        try {
            const template = templates.find(t => t.templateId === selectedTemplate);
            if (template) {
                const currentRecord = csvData[currentPreviewIndex];

                // Log for debugging
                console.log('Current Record:', currentRecord);
                console.log('Header Names Mapping:', headerNames);
                console.log('Mail Tags:', mailTags);

                const previewContent = replaceMailTagsWithData(template.templateContent, currentRecord);
                setPreviewTemplate(previewContent);
                setPreviewModalOpen(true);
            }
        } catch (error) {
            console.error('Error preparing preview:', error);
            displayError('Error preparing preview');
        }
    };
    const openTableModal = async () => {
        if (!selectedTemplate || csvData.length === 0) {
            displayError('Please select a template and upload data first.');
            return;
        }


        setTableModalopen(true);


    };

    const handlePreviewNavigation = async (direction) => {
        let newIndex;
        if (direction === 'prev') {
            newIndex = currentPreviewIndex > 0 ? currentPreviewIndex - 1 : csvData.length - 1;
        } else {
            newIndex = currentPreviewIndex < csvData.length - 1 ? currentPreviewIndex + 1 : 0;
        }
        setCurrentPreviewIndex(newIndex);

        const template = templates.find(t => t.templateId === selectedTemplate);
        if (template) {
            const currentRecord = csvData[newIndex];
            const previewContent = replaceMailTagsWithData(template.templateContent, currentRecord);
            setPreviewTemplate(previewContent);
        }
    };


    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };
    const openTempModal = () => {
        setempModalopen(true);
    };

    const closeTempModal = () => {
        setempModalopen(false);
    };

    const fetchCampaignNames = async () => {
        try {
            const campaignsResponse = await fetch(`${apiUrl}/Mailer/GetCampaignNames?BUId=${BUId}`);
            if (campaignsResponse.ok) {
                const campaignsData = await campaignsResponse.json();
                setCampaignNames(campaignsData);
            } else {
                displayError('Failed to fetch campaign names.');
            }
        } catch (error) {
            displayError('Error fetching data from the API.');
        } finally {
            setIsLoading(false);
        }
    };
    
    // 2. Update the handleSave function to do one of the following:
    const handleSave = async () => {
        if (!newCampaign.trim()) {
            displayError('Please enter a campaign name.');
            return;
        }
    
        setIsSaving(true);
        
        const campaignData = {
            CampaignName: newCampaign,
            BUId: sessionStorage.getItem("BUId"),
            CreatedOn: format(new Date(), 'dd/MM/yyyy hh:mm a'),
            CampaignId: ''
        };
    
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Mailer/CreateCampaign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(campaignData),
            });
    
            if (response.ok) {
                const result = await response.json();
                // Option 1: If the API returns the new campaign with ID
                if (result.campaignId) {
                    // Add the new campaign to the dropdown immediately
                    setCampaignNames(prevCampaigns => [
                        ...prevCampaigns, 
                        { 
                            campaignId: result.campaignId, 
                            campaignName: newCampaign 
                        }
                    ]);
                    // Optionally select the new campaign
                    setSelectedCampaign(result.campaignId);
                } else {
                    // Option 2: If the API doesn't return the ID, reload the campaign list
                    await fetchCampaignNames();
                }
    
                displayError('Campaign added successfully!');
                closeModal();
                setNewCampaign('');
            } else {
                displayError('Failed to save campaign. Please try again.');
            }
        } catch (error) {
            displayError('An error occurred while saving the campaign.');
            console.error('Save error:', error);
        } finally {
            setIsSaving(false);
        }
    };
    
    // 3. Update the useEffect to use the new fetchCampaignNames function
    useEffect(() => {
        fetchCampaignNames();
        
        const fetchTemplates = async () => {
            try {
                const response = await fetch(`${apiUrl}/Mailer/GetTemplateNames?BUId=${BUId}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch Templates');
                }
                const data = await response.json();
                setTemplates(data);
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };
        
        fetchTemplates();
    }, [apiUrl, BUId]);

    // const handleSave = async () => {
    //     const campaignData = {
    //         CampaignName: newCampaign,
    //         BUId: sessionStorage.getItem("BUId"),
    //         CreatedOn: format(new Date(), 'dd/MM/yyyy hh:mm a'),
    //         CampaignId: ''
    //     };

    //     try {
    //         const response = await fetch(`${process.env.REACT_APP_API_URL}/Mailer/CreateCampaign`, {
    //             method: 'POST',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //             },
    //             body: JSON.stringify(campaignData),
    //         });

    //         if (response.ok) {
    //             const result = await response.json();
    //             setCampaignName(newCampaign);
              
    //             closeModal();
      
    //         } else {
    //             alert('Failed to save campaign. Please try again.');
    //         }
    //     } catch (error) {
    //         alert('An error occurred while saving the campaign.');
    //     }
    // };


    // useEffect(() => {
    //     const fetchCampaignNames = async () => {
    //         try {
    //             const campaignsResponse = await fetch(`${apiUrl}/Mailer/GetCampaignNames?BUId=${BUId}`);
    //             if (campaignsResponse.ok) {
    //                 const campaignsData = await campaignsResponse.json();
    //                 setCampaignNames(campaignsData);
    //             } else {
    //                 displayError('Failed to fetch campaign names.');
    //             }
    //         } catch (error) {
    //             displayError('Error fetching data from the API.');
    //         } finally {
    //             setIsLoading(false);
    //         }
    //     };

    //     const fetchTemplates = async () => {
    //         try {
    //             const response = await fetch(`${apiUrl}/Mailer/GetTemplateNames?BUId=${BUId}`);
    //             if (!response.ok) {
    //                 throw new Error('Failed to fetch Templates');
    //             }
    //             const data = await response.json();
    //             console.log(data);
    //             setTemplates(data);

    //         } catch (error) {
    //             console.error('Error fetching templates:', error);
    //         }
    //     };

    //     fetchCampaignNames();
    //     fetchTemplates();
    // }, [apiUrl, BUId]);

    const displayError = (message) => {
        setErrorMessage(message);
        setTimeout(() => {
            setErrorMessage('');
        }, 5000);
    };

    const handleCampaignChange = (e) => {
        setSelectedCampaign(e.target.value);
    };

    const fetchMailTags = async () => {
        if (!selectedTemplate) {
            return [];
        }

        try {
            const response = await fetch(`${apiUrl}/Mailer/GetTemplatesById?BUId=${BUId}&TemplateId=${selectedTemplate}`);
            if (response.ok) {
                const data = await response.json();
                let mailTags = data.response.result[0].mailTags;

                // Check if "email" tag is already present
                if (!mailTags.includes('EmailAddress')) {
                    // Add "email" tag if it's not already in the array
                    mailTags.push('EmailAddress');
                }
                setMailTags(mailTags); // Update the state with the modified tags
                return mailTags;
            }
        } catch (error) {
            console.error('Error fetching mail tags:', error);
            return [];
        }
    };

    const handleHeaderClick = async (column) => {
        if (!selectedTemplate) {
            displayError('Please select a template first.');
            return;
        }

        setActiveHeader(column);
        setIsDropdownVisible(true);

        if (mailTags.length === 0) {
            await fetchMailTags();
        }
    };

    // const handleMailTagSelect = (tag) => {
    //     setSelectedMailTag(tag);
    //     setIsDropdownVisible(false);
    //     setHeaderNames(prevNames => ({
    //         ...prevNames,
    //         [activeHeader]: tag,
    //     }));
    // };

    const handleMailTagSelect = (tag) => {
        setSelectedMailTag(tag);
        setIsDropdownVisible(false);

        // Create a copy of the current header names
        let updatedHeaderNames = { ...headerNames };

        // When "Select a tag" is chosen, explicitly set to null
        if (tag === null) {
            updatedHeaderNames[activeHeader] = null;
            setHeaderNames(updatedHeaderNames);
            return;
        }

        // Check if this tag is already assigned to another header
        const existingHeader = Object.keys(updatedHeaderNames).find(
            headerKey => updatedHeaderNames[headerKey] === tag && headerKey !== activeHeader
        );

        // Clear the tag from any other header that's using it
        if (existingHeader) {
            updatedHeaderNames[existingHeader] = null;
        }

        // Assign the tag to our active header
        updatedHeaderNames[activeHeader] = tag;

        // Update the state
        setHeaderNames(updatedHeaderNames);
    };

    const isValidHeader = (header) => {
        return headerEnabled[header] && (
            headerNames[header] || // Header has a selected mail tag
            mailTags.includes(header) // Header matches a mail tag directly
        );
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!selectedTemplate) {
                displayError('Please select a template first.');
                e.target.value = '';
                return;
            }

            const tags = await fetchMailTags();

            Papa.parse(file, {
                complete: (result) => {
                    if (result.data.length === 0) {
                        displayError('The CSV file is empty.');
                    } else if (result.data[0] && Object.keys(result.data[0]).length > 0) {
                        setCsvData(result.data);

                        const newHeaderNames = {};
                        Object.keys(result.data[0]).forEach(header => {
                            if (tags.includes(header)) {
                                newHeaderNames[header] = header;
                            }
                        });
                        setHeaderNames(newHeaderNames);
                    } else {
                        displayError('Invalid CSV format.');
                    }
                },
                error: (error) => {
                    displayError('Error reading the CSV file.');
                },
                header: true,
                skipEmptyLines: true,
            });
        }
    };
    const filterValidData = (data) => {
        return data.map(row => {
            const validRow = {};
            Object.keys(row).forEach(header => {
                // if (isValidHeader(header)) {
                const headerKey = headerNames[header] || header;
                validRow[headerKey] = row[header];

                // }
            });
            return validRow;
        });
    };

    // const validateData = () => {
    //     const hasValidHeaders = Object.keys(csvData[0] || {}).some(header => isValidHeader(header));
    //     if (!hasValidHeaders) {
    //         displayError('No valid columns selected. Please select at least one valid column (green) with checkbox checked.');
    //         return false;
    //     }
    //     if (!selectedCampaign || !selectedTemplate) {
    //         displayError('Please select campaign and template.');
    //         return false;
    //     }

    //     return true;
    // };

    const validateData = () => {
        // Check if campaign and template are selected
        if (!selectedCampaign || !selectedTemplate) {
            displayError('Please select campaign and template.');
            return false;
        }

        // Check if there is any CSV data
        if (!csvData.length) {
            displayError('Please upload CSV data.');
            return false;
        }

        // Check if any headers are enabled
        const hasEnabledHeaders = Object.values(headerEnabled).some(enabled => enabled);
        if (!hasEnabledHeaders) {
            displayError('Please enable at least one column.');
            return false;
        }

        // Check if all mail tags are mapped
        const unmappedTags = mailTags.filter(tag => {
            // Check if this tag exists in headerNames values
            return !Object.values(headerNames).includes(tag);
        });

        if (unmappedTags.length > 0) {
            displayError(`Please map all required mail tags. Missing tags: ${unmappedTags.join(', ')}`);
            return false;
        }

        // Check if enabled headers are valid
        const hasValidHeaders = Object.keys(csvData[0] || {}).some(header => isValidHeader(header));
        if (!hasValidHeaders) {
            displayError('No valid columns selected. Please select at least one valid column (green) with checkbox checked.');
            return false;
        }

        return true;
    };

    const handleSaveContacts = async () => {
        if (!validateData()) {
            return;
        }

        setIsSaving(true);

        try {

            const filteredData = filterValidData(csvData);
            const contactDetails = filteredData.map(contact => {
                contact.IsSent = 0;
                contact.IsUnSubscribe = 0;
                return JSON.stringify(contact);
            });
            const payload = {
                BUId: BUId,
                ContactDetails: contactDetails,
                CampaignId: selectedCampaign,
                TemplateId: selectedTemplate,
                CreatedOn: format(new Date(), 'dd/MM/yyyy hh:mm a'),
            };

            // Log the filtered data being sent (for debugging)
            console.log('Sending filtered data:', filteredData);

            const response = await fetch(`${apiUrl}/Mailer/SaveContactDetails`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                displayError('Contacts added successfully!');
                setCsvData([]);
                setSelectedCampaign('');
                setSelectedTemplate('');
                setHeaderNames({});
                setHeaderEnabled({});
                document.getElementById('csv-upload').value = '';
            } else {
                const errorDetails = await response.text();
                displayError(`Error saving contacts: ${errorDetails}`);
            }
        } catch (error) {
            console.error('Save error:', error);
            displayError('Error occurred while saving contacts.');
        } finally {
            setIsSaving(false);
        }
    };

    const getValidRecordsCount = () => {
        if (csvData.length === 0) return 0;
        const filteredData = filterValidData(csvData);
        return filteredData.filter(row => Object.keys(row).length > 0).length;
    };

    // const handleSaveContacts = async () => {
    //     if (!selectedCampaign || !selectedTemplate) {
    //         displayError('Please select all fields.');
    //         return;
    //     }

    //     setIsSaving(true);

    //     try {
    //         const contactDetails = csvData.map(contact => JSON.stringify(contact));
    //         const payload = {
    //             BUId: BUId,
    //             ContactDetails: contactDetails,
    //             CampaignId: selectedCampaign,
    //             TemplateId: selectedTemplate,
    //             CreatedOn: format(new Date(), 'dd/MM/yyyy hh:mm a'),
    //         };

    //         const response = await fetch(`${apiUrl}/Mailer/SaveContactDetails`, {
    //             method: 'POST',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //             },
    //             body: JSON.stringify(payload),
    //         });

    //         if (response.ok) {
    //             displayError('Contacts added successfully!');
    //             setCsvData([]);
    //             setSelectedCampaign('');
    //             setSelectedTemplate('');
    //             document.getElementById('csv-upload').value = '';
    //         } else {
    //             const errorDetails = await response.text();
    //             displayError(`Error saving contacts: ${errorDetails}`);
    //         }
    //     } catch (error) {
    //         displayError('Error occurred while saving contacts.');
    //     } finally {
    //         setIsSaving(false);
    //     }
    // };



    const getHeaderBgColor = (header) => {
        // If header is disabled, show gray
        if (!headerEnabled[header]) {
            return 'bg-gray-400 text-white';
        }
        // If header has a selected mail tag mapping, show green
        if (headerNames[header]) {
            return 'bg-green-500 text-white';
        }
        if (headerNames[header] === null) {
            return 'bg-red-500 text-white';
        }

        // If header directly matches a mail tag, show green
        if (mailTags.includes(header)) {
            return 'bg-green-500 text-white';
        }

        // Default case - no mapping ("Select a tag"), show red
        return 'bg-red-500 text-white';
    };

    // useEffect(() => {
    //     if (selectedTemplate && csvData.length > 0) {
    //         fetchMailTags().then(tags => {
    //             const newHeaderNames = {};
    //             Object.keys(csvData[0]).forEach(header => {
    //                 if (tags.includes(header)) {
    //                     newHeaderNames[header] = header;
    //                 }
    //             });
    //             setHeaderNames(newHeaderNames);
    //         });
    //     }
    //     if (csvData.length > 0) {
    //         const initialHeaderState = {};
    //         Object.keys(csvData[0]).forEach(header => {
    //             initialHeaderState[header] = true;
    //         });
    //         setHeaderEnabled(initialHeaderState);
    //     }
    // }, [selectedTemplate, csvData]);

    useEffect(() => {
        if (selectedTemplate && csvData.length > 0) {
            fetchMailTags().then(tags => {
                const newHeaderNames = {};
                Object.keys(csvData[0]).forEach(header => {
                    if (tags.includes(header)) {
                        newHeaderNames[header] = header;
                    }
                    // Leave other headers as undefined, not "Select a tag"
                });
                setHeaderNames(newHeaderNames);
            });
        }
        if (csvData.length > 0) {
            const initialHeaderState = {};
            Object.keys(csvData[0]).forEach(header => {
                initialHeaderState[header] = true;
            });
            setHeaderEnabled(initialHeaderState);
        }
    }, [selectedTemplate, csvData]);
    const handleCheckboxChange = (header, event) => {
        event.stopPropagation();
        setHeaderEnabled(prev => ({
            ...prev,
            [header]: !prev[header]
        }));
    };

    const getAvailableMailTags = () => {
        const selectedTags = Object.values(headerNames);
        // return mailTags.filter(tag => !selectedTags.includes(tag));
        return mailTags
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="overflow-x-auto">
            <h3 className="text-2xl font-bold text-gray-900">IMPORT CONTACTS</h3><br/>
            <div className="flex flex-wrap mb-4">
                <div className="flex-1 max-w-[48%] mr-4">
                    <label htmlFor="campaign-name" className="text-gray-700">Campaign Name</label>
                    <select
                        id="campaign-name"
                        value={selectedCampaign}
                        onChange={handleCampaignChange}
                        className="border py-2 px-4 rounded-md w-full my-2"
                        disabled={isLoading}
                    >
                        <option value="">Select Campaign</option>
                        {campaignNames.map((campaign) => (
                            <option key={campaign.campaignId} value={campaign.campaignId}>
                                {campaign.campaignName}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center space-x-1 m-4">
                    {/* <label htmlFor="campaign-name" className="text-gray-700 font-medium mt-3">Create Campaign</label> */}
                    <button
                        className="inline-flex items-center justify-center p-2 mt-4 mt-1 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        onClick={openModal}
                    >
                        <Plus className="w-5 h-5 text-gray-600" />
                    </button>
                </div>
                {isModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
                        <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
                            <h3 className="text-lg font-semibold mb-4">Create New Campaign</h3>
                            <div className="mb-4">
                                <label htmlFor="new-campaign-name" className="block text-sm font-medium text-gray-700 mb-2">
                                    Campaign Name
                                </label>
                                <input
                                    id="new-campaign-name"
                                    type="text"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                                    value={newCampaign}
                                    onChange={(e) => setNewCampaign(e.target.value)}
                                    placeholder="Enter campaign name"
                                />
                            </div>
                            <div className="flex justify-end space-x-2">
                                <button
                                    onClick={closeModal}
                                    className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex-1 max-w-[48%] mr-4">
                    <label htmlFor="template-name" className="text-gray-700">Template Name</label>
                    <div className="flex items-center gap-2">
                        <select
                            id="templateName"
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            className="border py-2 px-4 rounded-md w-full my-2"
                            disabled={isLoading}
                        >
                            <option value="">Select Template</option>
                            {templates.map((template) => (
                                <option key={template.templateId} value={template.templateId}>
                                    {template.templateName}
                                </option>
                            ))}
                        </select>

                        <Eye
                            className="text-gray-500 cursor-pointer"
                            onClick={openTempModal}
                        />
                        {/* Modal */}
                        {tempModalopen && selectedTemplate && (
                            <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
                                <div className="bg-white p-6 w-3/4 max-w-5xl h-auto rounded-lg shadow-lg">
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-xl font-semibold">

                                            {templates.find(template => template.templateId === selectedTemplate)?.templateName}
                                        </h2>
                                        <button onClick={closeTempModal} className="text-red-500 text-xl font-bold">X</button>
                                    </div>
                                    <div className="mt-4">
                                        <div
                                            className="template-content"
                                            style={{ maxHeight: '500px', overflowY: 'scroll' }}
                                            dangerouslySetInnerHTML={{
                                                __html: templates.find(template => template.templateId === selectedTemplate)?.templateContent
                                            }}
                                        />
                                    </div>
                                    <div className="mt-4 text-center">
                                        <button onClick={closeTempModal} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>


                <div className="flex-1 max-w-[48%]">
                    <label htmlFor="csv-upload" className="text-gray-700">Upload Data</label>
                    <input
                        id="csv-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="border py-2 px-4 rounded-md w-full my-2"
                    />
                </div>
            </div>

            {csvData.length > 0 && (
                <div className="mt-4">
                    <table className="w-full border-collapse mt-4">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-500 to-purple-600">
                                {Object.keys(csvData[0] || {}).map((key) => (
                                    <th
                                        key={key}
                                        className={`px-4 py-3 text-left text-sm font-semibold cursor-pointer ${getHeaderBgColor(key)}`}
                                        onClick={() => headerEnabled[key] && handleHeaderClick(key)}
                                    >
                                        <span className="flex justify-between items-center w-full">
                                            {/* <span className="flex items-center">
                                                {headerNames[key] || key}
                                                {headerEnabled[key] && (
                                                    <Edit2 className="ml-2 text-sm text-gray-300" />
                                                )}
                                            </span> */}
                                            {/* <span className="flex items-center">
    {headerNames[key] || (headerEnabled[key] ? "Select a tag" : key)}
    {headerEnabled[key] && (
        <Edit2 className="ml-2 text-sm text-gray-300" />
    )}
</span> */}

                                            <span className="flex items-center">
                                                {headerNames[key] !== null && headerNames[key] !== undefined
                                                    ? headerNames[key]
                                                    : (headerEnabled[key] ? "Select a tag" : key)}
                                                {headerEnabled[key] && (
                                                    <Edit2 className="ml-2 text-sm text-gray-300" />
                                                )}
                                            </span>
                                            {/* <input
                                                type="checkbox"
                                                className="mr-2"
                                                checked={headerEnabled[key]}
                                                onChange={(e) => handleCheckboxChange(key, e)}
                                                onClick={(e) => e.stopPropagation()}
                                            /> */}
                                        </span>


                                        {activeHeader === key && isDropdownVisible && headerEnabled[key] && (
                                            <div
                                                ref={dropdownRef}
                                                className="absolute mt-2 bg-white border rounded shadow-lg w-48 z-10"
                                            >
                                                <ul className="py-2">
                                                    {getAvailableMailTags().map((tag, index) => (
                                                        <li
                                                            key={index}
                                                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMailTagSelect(tag);
                                                            }}
                                                        >
                                                            {tag}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {csvData.slice(0, 5).map((row, index) => (
                                <tr key={index} className="hover:bg-gray-50 border-b">
                                    {Object.keys(csvData[0] || {}).map((key, i) => (
                                        <td key={i} className="px-4 py-3 text-gray-800">
                                            {row[key] || ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {csvData.length > 0 && (
                <div className="mt-4">
                    <div className="flex justify-between items-center">

                        <div className="flex gap-2" >
                            <button
                                onClick={handleSaveContacts}
                                className={`py-2 px-4 rounded-md text-white ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'
                                    }`}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'ADD'}
                            </button>
                            <button
                                onClick={openPreviewModal}
                                className="py-2 px-4 rounded-md text-white bg-blue-500 hover:bg-blue-600"
                            >
                                Email Preview
                            </button>

                        </div>
                        <div className="flex gap-2 pt-5">
                            {/* <div className="text-sm text-gray-600">
                            Valid records to be saved: {getValidRecordsCount()} / {csvData.length} */}
                            <div className="text-sm text-gray-600">
                                Total Records : {getValidRecordsCount()}
                            </div>
                            <button
                                onClick={openTableModal}
                                className="py-2 px-4 rounded-md text-white bg-yellow-400 hover:bg-yellow-600"
                            >
                                View
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Preview Modal */}
            {previewModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Email Preview</h2>
                            <button
                                onClick={() => setPreviewModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ×
                            </button>
                        </div>

                        {/* Email Header */}
                        <div className="border-b pb-4 mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center">
                                    <span className="font-semibold mr-2">To:</span>
                                    <span>{getEmailFromRecord(csvData[currentPreviewIndex])}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePreviewNavigation('prev')}
                                        className="p-1 rounded hover:bg-gray-100"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <span className="text-sm text-gray-600">
                                        {currentPreviewIndex + 1} of {csvData.length}
                                    </span>
                                    <button
                                        onClick={() => handlePreviewNavigation('next')}
                                        className="p-1 rounded hover:bg-gray-100"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Email Body */}
                        <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: previewTemplate }}
                        />

                        {/* Modal Footer */}
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setPreviewModalOpen(false)}
                                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {tableModalopen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-semibold text-gray-800">Uploaded Data</h3>
                                <button
                                    className="text-gray-500 hover:text-gray-700"
                                    onClick={() => setTableModalopen(false)}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>

                            {csvData.length > 0 && (
                                <>
                                    <div className="mt-4 overflow-x-auto">
                                        <table className="w-full border-collapse mt-4">
                                            <thead>
                                                <tr className="bg-gradient-to-r from-blue-500 to-purple-600">
                                                    {Object.keys(csvData[0] || {}).map((key) => (
                                                        <th
                                                            key={key}
                                                            className={`px-4 py-3 text-left text-sm font-semibold cursor-pointer ${getHeaderBgColor(key)}`}
                                                            onClick={() => headerEnabled[key] && handleHeaderClick(key)}
                                                        >
                                                            <span className="flex justify-between items-center w-full">
                                                                <span className="flex items-center">
                                                                    {headerNames[key] || key}
                                                                    {headerEnabled[key] && (
                                                                        <Edit2 className="ml-2 text-sm text-gray-300" />
                                                                    )}
                                                                </span>
                                                            </span>
                                                            {activeHeader === key && isDropdownVisible && headerEnabled[key] && (

                                                                <div
                                                                    ref={dropdownRef}
                                                                    className="absolute mt-2 bg-white border rounded shadow-lg w-48 z-10"
                                                                >
                                                                    <ul className="py-2">
                                                                        <li
                                                                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer border-b"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleMailTagSelect(null); // Pass null to indicate removing the tag
                                                                            }}
                                                                        >
                                                                            Select a tag
                                                                        </li>
                                                                        {getAvailableMailTags().map((tag, index) => (
                                                                            <li
                                                                                key={index}
                                                                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleMailTagSelect(tag);
                                                                                }}
                                                                            >
                                                                                {tag}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>



                                                            )}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedData.map((row, index) => (
                                                    <tr key={index} className="hover:bg-gray-50 border-b">
                                                        {Object.keys(csvData[0] || {}).map((key, i) => (
                                                            <td key={i} className="px-4 py-3 text-gray-800">
                                                                {row[key] || ''}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Controls */}
                                    <div className="mt-6 flex items-center justify-between">
                                        <div className="flex items-center text-sm text-gray-500">
                                            Showing
                                            <span className="font-medium mx-1">
                                                {Math.min(currentPage * itemsPerPage - itemsPerPage + 1, csvData.length)}
                                            </span>
                                            to
                                            <span className="font-medium mx-1">
                                                {Math.min(currentPage * itemsPerPage, csvData.length)}
                                            </span>
                                            of
                                            <span className="font-medium mx-1">{csvData.length}</span>
                                            entries
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <select
                                                className="px-3 py-1 border rounded text-sm"
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>

                                            <button
                                                className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(1)}
                                            >
                                                First
                                            </button>
                                            <button
                                                className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            >
                                                Previous
                                            </button>

                                            <div className="flex items-center">
                                                {getPageNumbers().map(pageNum => (
                                                    <button
                                                        key={pageNum}
                                                        className={`w-8 h-8 flex items-center justify-center rounded ${currentPage === pageNum ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                                                            }`}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                ))}
                                            </div>

                                            <button
                                                className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            >
                                                Next
                                            </button>
                                            <button
                                                className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(totalPages)}
                                            >
                                                Last
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {errorMessage && (
                <div className="mt-4 text-red-500 text-center">
                    {errorMessage}
                </div>
            )}

            {/* {csvData.length > 0 && (
                <div className="mt-4 text-right">
                    <button
                        onClick={handleSaveContacts}
                        className={`py-2 px-4 rounded-md text-white ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'}`}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'ADD'}
                    </button>
                </div>
            )} */}
        </div>
    );
}

export default CreateContacts;